import React from "react";
import { VisuallyHidden } from "react-aria";
import { t } from "ttag";

import type { MainNavBlock, SubmenuContentBlock } from "@reactivated";

import { Clickable } from "../Clickables";
import { Svg } from "../Svg";
import { LinkList, normalizeLinks } from "./LinkList";
import { NavAd } from "./NavAd";

import styles from "./MobileSubmenu.module.scss";

interface ItemProps {
    item: SubmenuContentBlock[number];
    expandedBlockId: string | null;
    onCollapsedListSelect: (blockId: string) => void;
}

const MobileSubmenuItem = ({
    item,
    expandedBlockId,
    onCollapsedListSelect,
}: ItemProps) => {
    switch (item.type) {
        case "link_list":
        case "link_list_page_tree":
        case "link_list_facet": {
            const collapseOnMobile = item.value.collapse_on_mobile ?? false;
            if (collapseOnMobile && item.value.title) {
                return (
                    <li className={styles.fullColumn}>
                        <Clickable
                            className={styles.collapsedListTrigger}
                            onPress={() => onCollapsedListSelect(item.id)}
                            aria-expanded={expandedBlockId === item.id}
                        >
                            <span>{item.value.title}</span>
                            <Svg
                                name="caret-right"
                                className={styles.caretIcon}
                            />
                        </Clickable>
                    </li>
                );
            }
            return (
                <li className={styles.fullColumn}>
                    <LinkList
                        title={item.value.title ?? null}
                        links={normalizeLinks(item)}
                        id={item.id}
                    />
                </li>
            );
        }
        case "nav_ad":
            return (
                <li
                    className={
                        item.value.full_width_mobile
                            ? styles.fullColumn
                            : styles.halfColumn
                    }
                >
                    <NavAd item={item.value} />
                </li>
            );
    }
};

interface BackPanelProps {
    title: string;
    /**
     * Created with MobileSubmenuCollapsedList in mind, to preserve an
     * aria-labelledby inside of LinkList
     */
    titleId?: string;
    onBack: () => void;
    children: React.ReactNode;
}

const BackPanel = ({ title, titleId, onBack, children }: BackPanelProps) => (
    <div className={styles.root}>
        <Clickable className={styles.backHeader} onPress={onBack}>
            <Svg name="caret-right" className={styles.backCaret} />
            <VisuallyHidden>{t`Back to: `}</VisuallyHidden>
            <span id={titleId}>{title}</span>
        </Clickable>
        {children}
    </div>
);

interface Props {
    item: Extract<MainNavBlock[number], { type: "submenu" }>;
    expandedBlockId: string | null;
    onBack: () => void;
    onCollapsedListSelect: (blockId: string) => void;
}

export const MobileSubmenu = ({
    item,
    expandedBlockId,
    onBack,
    onCollapsedListSelect,
}: Props) => {
    const items = item.value.items ?? [];
    if (!item.value.title) return <></>;

    return (
        <BackPanel title={item.value.title} onBack={onBack}>
            <ul className={styles.content}>
                {items.map((contentItem) => (
                    <MobileSubmenuItem
                        key={contentItem.id}
                        item={contentItem}
                        expandedBlockId={expandedBlockId}
                        onCollapsedListSelect={onCollapsedListSelect}
                    />
                ))}
            </ul>
        </BackPanel>
    );
};

interface CollapsedProps {
    block: Extract<
        SubmenuContentBlock[number],
        { type: "link_list" | "link_list_page_tree" | "link_list_facet" }
    >;
    onBack: () => void;
}

/**
 * In the CMS, there's an option for link lists to "collapse on mobile". Instead
 * of being a regular link list with a title and then a list of links, the title
 * becomes a trigger to open an extra submenu that has just the links.
 */
export const MobileSubmenuCollapsedList = ({
    block,
    onBack,
}: CollapsedProps) => {
    if (!block.value.title) return <></>;

    return (
        <BackPanel title={block.value.title} titleId={block.id} onBack={onBack}>
            <div className={styles.content}>
                <div className={styles.fullColumn}>
                    <LinkList
                        title={null}
                        links={normalizeLinks(block)}
                        id={block.id}
                    />
                </div>
            </div>
        </BackPanel>
    );
};
